import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BalanceHistory } from '@tradingblock/types';
import dayjs from 'dayjs';

interface ChartTooltipProps {
  point: { x: number; y: number };
  data: {
    date: string;
    price: number;
    historyItem?: BalanceHistory;
  };
  visible: boolean;
  chartBounds: DOMRect;
}

export const ChartTooltip: React.FC<ChartTooltipProps> = ({ point, data, visible, chartBounds }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tooltipRef.current || !visible) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const padding = 15;

    // Calculate position relative to chart container
    let left = point.x + padding;
    let top = point.y - tooltipRect.height / 2;

    // Check horizontal overflow
    if (left + tooltipRect.width > chartBounds.width) {
      left = point.x - tooltipRect.width - padding;
    }

    // Check vertical overflow
    if (top + tooltipRect.height > chartBounds.height) {
      top = chartBounds.height - tooltipRect.height - padding;
    }
    if (top < 0) {
      top = padding;
    }

    tooltip.style.transform = `translate(${left}px, ${top}px)`;
  }, [point, visible, chartBounds]);

  if (!visible) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        backgroundColor: '#33415e',
        color: '#FFFFFF',
        padding: '10px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      <strong>{data.date}</strong>
      <br />
      Account Value: ${data.price.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
      {data.historyItem &&
        data.historyItem.cashieringTransfers &&
        data.historyItem.cashieringTransfers.map((transfer, i) => (
          <div key={i} style={{ color: transfer.direction === 'Outgoing' ? '#ed6767' : '#02fff6' }}>
            {transfer.direction === 'Outgoing' ? 'Withdrawal' : 'Deposit'}: $
            {transfer.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          </div>
        ))}
      {data.historyItem &&
        data.historyItem.dividends &&
        data.historyItem.dividends.map((dividend, i) => (
          <div key={i} style={{ color: '#ffcd7e' }}>
            Dividend: ${dividend.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (
            {dividend.description || dividend.identifier || 'Unknown'})
          </div>
        ))}
    </div>,
    chartBounds.height ? document.querySelector('.chart-container') || document.body : document.body
  );
};
